import { inject, InjectionToken, Provider, signal, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { SetSelectedItemId } from '../state/cxp-crl-detail.actions';
import { CrlPanelTab } from '../models';
import {
  CXP_DEFAULT_PANEL_TAB,
  CXP_QUERY_PARAM_PANEL_TAB,
  CXP_ROUTE_OUTLET_PANEL,
  CXP_ROUTE_TOKEN_ITEM_DETAIL,
} from '../models/routing-constants';

/**
 * The query param value for a tab, or null for the default one — an ordinary
 * open leaves no `tab` in the URL rather than spelling out the default.
 */
function tabParam(tab: CrlPanelTab | undefined): string | null {
  return tab && tab !== CXP_DEFAULT_PANEL_TAB ? tab : null;
}

/**
 * How the CRL list page and its item panel open and close each other. The page
 * has a route in the authenticated flow and none in the secure-link one, so the
 * mechanism is swappable; both implementations land on the same
 * `selectedItemId` state.
 */
export interface CrlPanelNavigator {
  /** `tab` asks the panel to open on that tab; it is consumed once. */
  openItem(itemId: number, tab?: CrlPanelTab): void;
  closeItem(): void;
}

export const CRL_PANEL_NAVIGATOR = new InjectionToken<CrlPanelNavigator>('CRL_PANEL_NAVIGATOR');

/**
 * Adds `(panel:item/:itemId)` beside the list's own segment so the list stays
 * mounted. Must be called in an injection context — `relativeTo` is resolved
 * there and captured.
 */
export function routedCrlPanelNavigator(relativeTo: ActivatedRoute | null = inject(ActivatedRoute)): CrlPanelNavigator {
  const router = inject(Router);

  return {
    openItem(itemId: number, tab?: CrlPanelTab): void {
      router.navigate([{ outlets: { [CXP_ROUTE_OUTLET_PANEL]: [CXP_ROUTE_TOKEN_ITEM_DETAIL, itemId] } }], {
        relativeTo,
        queryParams: { [CXP_QUERY_PARAM_PANEL_TAB]: tabParam(tab) },
        queryParamsHandling: 'merge',
      });
    },
    closeItem(): void {
      // The tab is meaningless without an item, so it leaves with the panel.
      router.navigate([{ outlets: { [CXP_ROUTE_OUTLET_PANEL]: null } }], {
        relativeTo,
        queryParams: { [CXP_QUERY_PARAM_PANEL_TAB]: null },
        queryParamsHandling: 'merge',
      });
    },
  };
}

/**
 * Writes the selection straight to the state the display selectors read, and
 * exposes it as a signal so a host can render `<cxp-crl-panel>` itself.
 */
export class HostedCrlPanelNavigator implements CrlPanelNavigator {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly _selectedItemId = signal<number | null>(null);

  readonly selectedItemId: Signal<number | null> = this._selectedItemId.asReadonly();

  openItem(itemId: number, tab?: CrlPanelTab): void {
    this._selectedItemId.set(itemId);
    this.store.dispatch(new SetSelectedItemId(itemId));
    this.writeTabParam(tabParam(tab));
  }

  closeItem(): void {
    this._selectedItemId.set(null);
    this.store.dispatch(new SetSelectedItemId(null));
    this.writeTabParam(null);
  }

  /**
   * The hosted panel has no route of its own, but the page it sits on does, so
   * the tab still belongs in the URL and still survives a refresh.
   */
  private writeTabParam(tab: string | null): void {
    this.router.navigate([], {
      queryParams: { [CXP_QUERY_PARAM_PANEL_TAB]: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}

export function provideHostedCrlPanelNavigator(): Provider[] {
  return [
    HostedCrlPanelNavigator,
    { provide: CRL_PANEL_NAVIGATOR, useExisting: HostedCrlPanelNavigator },
  ];
}
